/**
 * Glass-overlay position/hover/rect reporters and bridge bootstrap tail.
 * Verbatim slice of the iframe bridge script (optimization Phase 10c) —
 * returned text is embedded inside the bridge IIFE; it is NOT module code.
 * The slice is interpolation-free: keep it that way (head/tail interpolations
 * live in iframe-bridge.js).
 */

export function overlayReportersLayer() {
  return String.raw`  // --- Glass overlay: section position + hover reporters ---

  // Uses dirty-flag + rAF batching: at most 1 report per frame.
  var _positionsDirty = false;
  var _positionsRafId = null;

  function reportSectionPositions() {
    _positionsRafId = null;
    _positionsDirty = false;
    _syncSectionEdgeState();

    var wrappers = document.querySelectorAll('[data-pb-instance]');
    var positions = [];
    var scrollY = window.scrollY;
    var zoom = getCurrentZoom();

    for (var i = 0; i < wrappers.length; i++) {
      var w = wrappers[i];
      var rect = w.getBoundingClientRect();
      var entry = {
        instanceId: w.getAttribute('data-pb-instance'),
        top: rect.top + scrollY,    // document-relative
        height: rect.height,
        category: w.getAttribute('data-pb-category') || '',
        label: w.getAttribute('data-pb-label') || w.getAttribute('data-pb-category') || 'Section',
        shared: w.hasAttribute('data-pb-shared')
      };

      // Include computed spacing for non-shared sections (used by spacing handles)
      if (!entry.shared) {
        var inner = w.firstElementChild; // the <section> element inside the wrapper
        if (inner) {
          var cs = getComputedStyle(inner);
          var wrapStyles = getComputedStyle(w);
          entry.spacing = {
            paddingTop: parseFloat(cs.paddingTop) || 0,
            paddingBottom: parseFloat(cs.paddingBottom) || 0,
            marginTop: parseFloat(wrapStyles.marginTop) || 0,
            marginBottom: parseFloat(wrapStyles.marginBottom) || 0
          };
        }
      }

      positions.push(entry);
    }

    send('section-positions', {
      positions: positions,
      contentHeight: getDocumentContentHeight(),
      // The visible slice of the document. contentHeight alone says how long
      // the page is; a minimap/rail viewport band also needs how much of it is
      // on screen, and the parent cannot read it from a sandboxed iframe.
      viewportHeight: window.innerHeight,
      scrollY: scrollY,
      zoom: zoom
    });
  }

  function markPositionsDirty() {
    _positionsDirty = true;
    if (!_positionsRafId) {
      _positionsRafId = requestAnimationFrame(function() {
        if (_positionsDirty) reportSectionPositions();
      });
    }
  }

  // Fire on scroll (rAF-throttled for smooth overlay sync)
  window.addEventListener('scroll', markPositionsDirty, { passive: true });

  // Fire on resize
  window.addEventListener('resize', markPositionsDirty, { passive: true });

  // Fire on DOM mutations (section add/remove/reorder)
  var mainEl = document.querySelector('main');
  var _mutObs = null;
  if (mainEl) {
    _mutObs = new MutationObserver(function(mutations) {
      if (_pbOnlyIgnoredMutations(mutations)) return;
      markPositionsDirty();
      markElementRectDirty();
      markHoverTagDirty();
    });
    _mutObs.observe(mainEl, { childList: true, subtree: true });
  }

  // Fire on body resize (section content height changes)
  var _resObs = new ResizeObserver(function() {
    markPositionsDirty();
    markElementRectDirty();
    markHoverTagDirty();
  });
  observeResizeTarget(_resObs, document.body);
  observeResizeTarget(_resObs, mainEl);
  observeResizeTarget(_resObs, window.__PB_SCROLL_ROOT__);

  // Disconnect observers on iframe teardown.
  // Primary cleanup: the iframe is destroyed and recreated on each render (shell.js renderPreview),
  // which implicitly GCs all listeners and observers. beforeunload is belt-and-suspenders
  // for edge cases like internal navigation within the iframe.
  window.addEventListener('beforeunload', function() {
    if (_mutObs) _mutObs.disconnect();
    _resObs.disconnect();
    if (_elementRectResObs) _elementRectResObs.disconnect();
    disconnectSelectedEditableObserver();
  });

  // Respond to explicit position queries from parent (nonce-validated)
  window.addEventListener('message', function(e) {
    var msg = e.data;
    if (msg && msg.source === 'pb-parent' && msg.nonce === __PB_NONCE__ && msg.type === 'query-section-positions') {
      reportSectionPositions();
    }
  });

  // --- Section hover reporter ---

  // Pointer events, filtered per EVENT rather than per device (D6): a hybrid
  // laptop reports hover:hover with a touchscreen attached, so a media query
  // would be wrong on exactly the machines that have both. A finger produces
  // pointerover too, and acting on it is what leaves a section highlighted
  // after the finger is long gone.
  document.addEventListener('pointerover', function(e) {
    if (e.pointerType && e.pointerType !== 'mouse') return;
    if (_previewMode) return;
    var wrapper = e.target.closest('[data-pb-instance]');
    var id = wrapper ? wrapper.getAttribute('data-pb-instance') : null;
    var elementHovered = !!(id && e.target.closest && e.target.closest('[data-pb-editable]'));
    var previousHoveredId = _hoveredSectionInstanceId;
    _setHoveredSection(id, { elementHovered: elementHovered });
    if (id !== previousHoveredId) {
      send('section-hovered', { instanceId: id });
    }
  }, { passive: true });

  document.addEventListener('pointerout', function(e) {
    if (e.pointerType && e.pointerType !== 'mouse') return;
    if (e.relatedTarget === null || !document.contains(e.relatedTarget)) {
      if (_hoveredSectionInstanceId !== null) {
        _setHoveredSection(null);
        send('section-hovered', { instanceId: null });
      }
    }
  }, { passive: true });

  // --- Element hover reporter ---
  // Iframe CSS owns editable hover pills. This reporter now feeds the rare
  // parent fallback only for marker-host hover cases and legacy structural
  // diagnostics if _pickStructuralHoverTarget() is deliberately re-enabled.
  var _hoveredTagElement = null;
  var _hoverTagDirty = false;
  var _hoverTagRafId = null;

  function _asElement(node) {
    if (!node) return null;
    if (node.nodeType === 1) return node;
    return node.parentElement || null;
  }

  function _pickStructuralHoverTarget(el, wrapper) {
    // Structural hover labels (Container, Row, Grid, etc.) are disabled —
    // they add visual clutter without actionable controls for pre-built stitches.
    // Only editables (handled by the caller) get hover labels.
    return null;
  }

  function _resolveHoverTarget(node) {
    var el = _asElement(node);
    if (!el) return null;
    var wrapper = el.closest('[data-pb-instance]');
    if (!wrapper) return null;
    var editable = el.closest('[data-pb-editable]');
    if (editable) return editable;
    return _pickStructuralHoverTarget(el, wrapper);
  }

  function _editableDisplayLabel(target, editableType) {
    return _kindLabelOf(target);
  }

  function _hoverLabelFor(target, editableType) {
    return _editableDisplayLabel(target, editableType);
  }

  function reportHoveredElementTag() {
    _hoverTagRafId = null;
    _hoverTagDirty = false;
    if (!_hoveredTagElement || !document.contains(_hoveredTagElement)) {
      _hoveredTagElement = null;
      send('element-hovered', null);
      return;
    }

    var wrapper = _hoveredTagElement.closest('[data-pb-instance]');
    if (!wrapper) {
      _hoveredTagElement = null;
      send('element-hovered', null);
      return;
    }

    var editable = _hoveredTagElement.closest('[data-pb-editable]');
    var anchor = editable || _hoveredTagElement;
    var instanceId = wrapper.getAttribute('data-pb-instance') || '';
    if (!instanceId) {
      send('element-hovered', null);
      return;
    }

    var editableType = editable ? (editable.getAttribute('data-pb-editable') || '') : '';
    var eid = editable ? (editable.getAttribute('data-pb-eid') || '') : '';
    var rect = anchor.getBoundingClientRect();
    var hoverZoom = getCurrentZoom();

    var label = _editableDisplayLabel(anchor, editableType);
    var childRects = null;
    var resolvedHoverHost = editable ? _resolveSelectedStateHost(editable) : null;
    // Same reason as reportElementRect. data-pb-hover-label sits on every
    // editable, so this — the element actually under the pointer — is the only
    // place that knows which host is about to paint a hover nameplate.
    if (resolvedHoverHost && resolvedHoverHost.host) {
      _syncPillHostFlip(resolvedHoverHost.host);
    }

    // If hovering a container-type element, collect rects of direct children
    if (!editableType && anchor.children.length > 0) {
      var isContainer = /Container|Row|Stack|Grid|Navigation|Section/.test(label);
      if (!isContainer) {
        var cs = getComputedStyle(anchor);
        isContainer = cs.display.indexOf('flex') !== -1 || cs.display.indexOf('grid') !== -1;
      }
      if (isContainer) {
        childRects = [];
        for (var ci = 0; ci < anchor.children.length; ci++) {
          var child = anchor.children[ci];
          var cr = child.getBoundingClientRect();
          if (cr.width > 0 && cr.height > 0) {
            childRects.push({
              top: cr.top + window.scrollY,
              left: cr.left,
              width: cr.width,
              height: cr.height,
              label: _editableDisplayLabel(child, '')
            });
          }
        }
        if (childRects.length === 0) childRects = null;
      }
    }

    var requiresParentHoverPill = !!(resolvedHoverHost && resolvedHoverHost.requiresParentHoverPill);
    if (editable && editable === _selectedEditable) requiresParentHoverPill = false;

    send('element-hovered', {
      instanceId: instanceId,
      eid: eid,
      editableType: editableType,
      kind: _kindOf(editable),
      label: label,
      requiresParentHoverPill: requiresParentHoverPill,
      rect: {
        top: rect.top + window.scrollY,
        left: rect.left,
        width: rect.width,
        height: rect.height
      },
      childRects: childRects,
      zoom: hoverZoom
    });
  }

  function markHoverTagDirty() {
    if (!_hoveredTagElement) return;
    _hoverTagDirty = true;
    if (!_hoverTagRafId) {
      _hoverTagRafId = requestAnimationFrame(function() {
        if (_hoverTagDirty) reportHoveredElementTag();
      });
    }
  }

  function setHoveredElementTag(el) {
    if (el === _hoveredTagElement) return;
    _hoveredTagElement = el;
    if (!_hoveredTagElement) {
      send('element-hovered', null);
      return;
    }
    reportHoveredElementTag();
  }

  // --- Inert-region hover feedback (Tier 4 Phase 4) ---
  // Regions with no editable AND no adapter coverage get a transient
  // data-pb-inert-hover attribute so "not editable" is visually distinct from
  // a misclick. STRICTLY transient: cleared on hover-out, hover-to-editable,
  // any pointerdown (click, drag start), and preview mode — and it lives only
  // in the live iframe DOM, never in parent-side stored HTML.
  var _inertHoverEl = null;

  function _clearInertHover() {
    if (!_inertHoverEl) return;
    try { _inertHoverEl.removeAttribute('data-pb-inert-hover'); } catch (err) { /* detached */ }
    _inertHoverEl = null;
  }

  function _isAdapterCoveredLeaf(el) {
    // Anything inside a reorderable repeat item (cards, nav/footer links,
    // social icons, gallery pictures…) has whole-item actions — NOT inert.
    // Mirrors _reorderSpecForSource / _findReorderHandle guard semantics:
    // a spec only covers the leaf when its requiredAncestorSelector (region
    // scope) is satisfied AND the leaf is not inside rejectIfTargetClosest —
    // otherwise hover feedback and real actionability diverge.
    if (!el || !el.closest) return false;
    var specs = (typeof __PB_REORDER_SPECS__ !== 'undefined' && __PB_REORDER_SPECS__) || [];
    for (var i = 0; i < specs.length; i++) {
      var spec = specs[i];
      if (!spec || !spec.sourceSelector) continue;
      var item = null;
      try {
        item = el.closest(spec.sourceSelector);
      } catch (err) { continue; /* invalid selector — skip */ }
      if (!item) continue;
      if (spec.requiredAncestorSelector && !item.closest(spec.requiredAncestorSelector)) continue;
      if (spec.rejectIfTargetClosest) {
        try {
          if (el.closest(spec.rejectIfTargetClosest)) continue;
        } catch (err2) { /* invalid selector — treat as no reject */ }
      }
      return true;
    }
    return false;
  }

  function _setInertHover(el) {
    if (el === _inertHoverEl) return;
    _clearInertHover();
    if (!el) return;
    _inertHoverEl = el;
    try { el.setAttribute('data-pb-inert-hover', ''); } catch (err) { /* readonly node */ }
  }

  function _resolveInertHoverTarget(rawTarget) {
    var el = _asElement(rawTarget);
    if (!el) return null;
    if (!el.closest('[data-pb-instance]')) return null;   // outside sections
    if (el.closest('[data-pb-editable]')) return null;    // editable — not inert
    if (_isAdapterCoveredLeaf(el)) return null;           // repeat-item — has actions
    if (el.closest('svg')) return null;                   // svg internals
    if (el.hasAttribute('data-pb-section')) return null;  // section wrapper itself
    return el;
  }

  document.addEventListener('pointerover', function(e) {
    if (e.pointerType && e.pointerType !== 'mouse') return;
    if (_previewMode) {
      _clearInertHover();
      return;
    }
    var target = _resolveHoverTarget(e.target);
    setHoveredElementTag(target);
    _setInertHover(target ? null : _resolveInertHoverTarget(e.target));
  }, { passive: true });

  document.addEventListener('pointerout', function(e) {
    if (e.pointerType && e.pointerType !== 'mouse') return;
    var related = _asElement(e.relatedTarget);
    if (_inertHoverEl && (!related || !_inertHoverEl.contains(related))) {
      _setInertHover(related && !_previewMode ? _resolveInertHoverTarget(related) : null);
    }
    if (!_hoveredTagElement) return;
    if (related && _hoveredTagElement.contains(related)) return;
    var next = _resolveHoverTarget(related);
    if (next !== _hoveredTagElement) {
      setHoveredElementTag(next);
    }
  }, { passive: true });

  // Any pointerdown (selection click, drag start, reorder grab) clears the
  // inert cue immediately — it must never linger through an interaction.
  //
  // A non-mouse pointerdown ALSO hard-resets the section hover. The reporters
  // above ignore a finger, but a mouse session earlier in the same page life
  // can have latched a section — plug in a mouse, hover a section, unplug it,
  // and the highlight has nothing left to clear it. Touching the screen is an
  // unambiguous signal that whatever the mouse was pointing at is no longer
  // what the user means.
  document.addEventListener('pointerdown', function(e) {
    _clearInertHover();
    if (e && e.pointerType && e.pointerType !== 'mouse') {
      if (_hoveredSectionInstanceId !== null) {
        _setHoveredSection(null);
        send('section-hovered', { instanceId: null });
      }
      setHoveredElementTag(null);
    }
  }, { capture: true, passive: true });

  window.addEventListener('scroll', markHoverTagDirty, { passive: true });
  window.addEventListener('resize', markHoverTagDirty, { passive: true });

  // --- Glass overlay: continuous element rect tracking ---
  // Reports updated rect for the selected editable on scroll/resize/mutation
  // so floating panels stay anchored after layout changes.
  var _elementRectDirty = false;
  var _elementRectRafId = null;

  function reportElementRect() {
    _elementRectRafId = null;
    _elementRectDirty = false;
    if (!_selectedEditable) {
      return;
    }
    if (!document.contains(_selectedEditable)) {
      clearSelectedEditable('rect-target-detached');
      return;
    }
    var r = _selectedEditable.getBoundingClientRect();
    var clientRects = _selectedEditable.getClientRects ? _selectedEditable.getClientRects() : null;
    if ((clientRects && clientRects.length === 0) || ((r.width || 0) <= 0 && (r.height || 0) <= 0)) {
      clearSelectedEditable('rect-target-not-visible');
      return;
    }
    var sy = window.scrollY;
    var zoom = getCurrentZoom();
    var path = getEditablePath(_selectedEditable);
    if (!path) return;
    var rectStyle = getComputedStyle(_selectedEditable);
    var rectContainer = _selectedEditable.closest('.cs-container') || _selectedEditable.closest('[data-pb-instance]');
    var rectContainerRect = rectContainer ? rectContainer.getBoundingClientRect() : null;
    var rectPic = _selectedEditable.parentElement && _selectedEditable.parentElement.tagName === 'PICTURE'
      ? _selectedEditable.parentElement : null;
    if (!rectPic && _selectedEditable.closest) rectPic = _selectedEditable.closest('picture');
    var rectPicRect = rectPic ? rectPic.getBoundingClientRect() : null;
    var rectPicStyle = rectPic ? getComputedStyle(rectPic) : null;
    var resolvedRectHost = _resolveSelectedStateHost(_selectedEditable);
    // Whether the selected element's nameplate has room above it is a
    // measurement, and this reporter is where it belongs: it already runs on
    // scroll, resize and mutation — each of which can carry the host under the
    // canvas frame's top edge — and it has already measured this one element.
    if (resolvedRectHost && resolvedRectHost.host) {
      _syncPillHostFlip(resolvedRectHost.host);
    }
    var rectPayload = {
      instanceId: path.instanceId,
      eid: path.eid,
      targetRef: path.targetRef,
      editableType: path.editableType,
      kind: path.kind,
      tagName: (_selectedEditable.tagName || '').toUpperCase(),
      label: _editableDisplayLabel(_selectedEditable, path.editableType),
      requiresParentPill: !!(resolvedRectHost && resolvedRectHost.requiresParentPill),
      requiresParentHoverPill: !!(resolvedRectHost && resolvedRectHost.requiresParentHoverPill),
      rect: { top: r.top + sy, left: r.left, width: r.width, height: r.height },
      zoom: zoom,
      spacing: {
        paddingTop: rectStyle.paddingTop,
        paddingRight: rectStyle.paddingRight,
        paddingBottom: rectStyle.paddingBottom,
        paddingLeft: rectStyle.paddingLeft,
        marginTop: rectStyle.marginTop,
        marginRight: rectStyle.marginRight,
        marginBottom: rectStyle.marginBottom,
        marginLeft: rectStyle.marginLeft
      },
      spacingTemplate: getSpacingTemplateForEditable(_selectedEditable),
      positioning: {
        position: rectStyle.position,
        left: rectStyle.left,
        right: rectStyle.right
      },
      containerWidth: rectContainerRect ? rectContainerRect.width : null,
      containerLeft: rectContainerRect ? rectContainerRect.left : null,
      pictureRect: rectPicRect ? { top: rectPicRect.top + sy, left: rectPicRect.left, width: rectPicRect.width, height: rectPicRect.height } : null,
      picturePositioning: rectPicStyle ? {
        paddingTop: rectPicStyle.paddingTop,
        paddingRight: rectPicStyle.paddingRight,
        paddingBottom: rectPicStyle.paddingBottom,
        paddingLeft: rectPicStyle.paddingLeft,
        marginTop: rectPicStyle.marginTop,
        marginRight: rectPicStyle.marginRight,
        marginBottom: rectPicStyle.marginBottom,
        marginLeft: rectPicStyle.marginLeft,
        position: rectPicStyle.position,
        left: rectPicStyle.left,
        right: rectPicStyle.right
      } : null
    };
    rectPayload.ancestry = getAncestryForEditable(_selectedEditable);
    addLogoRectPayload(rectPayload, _selectedEditable, sy);
    send('element-rect-update', rectPayload);
  }

  function markElementRectDirty() {
    if (!_selectedEditable) return;
    _elementRectDirty = true;
    if (!_elementRectRafId) {
      _elementRectRafId = requestAnimationFrame(function() {
        if (_elementRectDirty) reportElementRect();
      });
    }
  }

  window.addEventListener('scroll', markElementRectDirty, { passive: true });
  window.addEventListener('resize', markElementRectDirty, { passive: true });
  window.addEventListener('resize', function() { markStyleSnapshotDirty('viewport'); }, { passive: true });
  var _elementRectResObs = new ResizeObserver(markElementRectDirty);
  observeResizeTarget(_elementRectResObs, document.body);
  observeResizeTarget(_elementRectResObs, mainEl);
  observeResizeTarget(_elementRectResObs, window.__PB_SCROLL_ROOT__);

  // Dark-mode scripts mutate body classes inside the iframe without a parent
  // event. Treat body/root class/style changes as style invalidations so an
  // open panel re-reads the same canonical StyleSnapshot channel.
  var _styleContextObserver = new MutationObserver(function(mutations) {
    for (var mi = 0; mi < mutations.length; mi++) {
      if (mutations[mi].type === 'attributes') {
        markStyleSnapshotDirty('style-context');
        return;
      }
    }
  });
  if (document.body) _styleContextObserver.observe(document.body, { attributes: true, attributeFilter: ['class', 'style'] });
  _styleContextObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class', 'style'] });

  // Clear selected editable on canvas/section click (no element)
  window.addEventListener('message', function(e) {
    var msg = e.data;
    if (msg && msg.source === 'pb-parent' && msg.nonce === __PB_NONCE__ && msg.type === 'clear-selected-editable') {
      clearSelectedEditable('parent-clear');
    }
  });

  // Apply icon tints on startup if enabled
  if (__PB_ICON_TINTING__) _applyIconTints();
  syncAllTextEditablePlaceholders();
  _syncSectionEdgeState();
  _syncEditableDisplayAttributes(document);
  _injectHeaderZoneMarkers();
  // The dark-mode class arrives in the MARKUP now (the renderer stamps the
  // saved choice onto <body>), so nothing has told the toggles about it. Sync
  // them here or a restored dark canvas announces its toggle as unpressed.
  _setDarkTogglePressed(document.body.classList.contains('dark-mode'));

  send('bridge-ready', null);
})();`;
}
